/* Version history policy (DOM-free). Each layout keeps up to MAX_VERSIONS
   snapshots. One is taken, at most every snapshotInterval(), of the layout
   as it was BEFORE an editing burst began — so "how it looked before I
   started messing with it" is always a restore away, without a snapshot per
   autosave. */
export const MAX_VERSIONS = 20;

let every = 2 * 60 * 1000;
/** Tests shorten this; 0 = snapshot on every save that changes something. */
export const setSnapshotInterval = (ms: number): void => { every = ms; };
export const snapshotInterval = (): number => every;

/** Snapshot now? First save for this layout this session, or the last snapshot is old enough. */
export const shouldSnapshot = (lastAt: number | null, now: number): boolean =>
  lastAt === null || now - lastAt >= every;

/** Nothing worth keeping (a just-created layout). */
export const isEmptyDoc = (d: { walls?: unknown[]; fixtures?: unknown[]; labels?: unknown[] }): boolean =>
  !(d.walls?.length || d.fixtures?.length || d.labels?.length);

/** Newest `max` stay; the rest go. */
export function prune<T extends { mtime: number }>(list: T[], max = MAX_VERSIONS): { keep: T[]; drop: T[] } {
  const sorted = [...list].sort((a, b) => b.mtime - a.mtime);
  return { keep: sorted.slice(0, max), drop: sorted.slice(max) };
}

/** "Just now" · "5 min ago" · "Today 14:03" · "Yesterday 09:12" · "Oct 30, 14:03" */
export function fmtWhen(mtimeSec: number, nowMs = Date.now()): string {
  const t = mtimeSec * 1000, diff = nowMs - t;
  if (diff < 60_000) return 'Just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  const d = new Date(t), n = new Date(nowMs);
  const hm = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, n)) return `Today ${hm}`;
  const y = new Date(nowMs - 86_400_000);
  if (sameDay(d, y)) return `Yesterday ${hm}`;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${hm}`;
}
